/**
 * @fileoverview useMonthNavigation - Month selection and navigation hook.
 *
 * Manages the currently selected month and provides navigation controls.
 * Handles month boundaries, close status, and budget start date constraints.
 *
 * @description This hook provides:
 * - Month selection state (Date object)
 * - Previous/next month navigation with boundary checks
 * - Month close/open operations (owner only)
 * - Display formatting utilities
 * - Automatic refresh on budget context change
 *
 * Navigation boundaries:
 * - Cannot navigate before budget start month
 * - Cannot navigate beyond current month
 *
 * Month closing:
 * - Only budget owners can close months
 * - Closed months prevent non-owner transaction changes
 * - Close status is fetched from backend
 *
 * @module hooks/useMonthNavigation
 */

import { useState, useMemo, useEffect } from 'react';
import {
    getCurrentMonth,
    getMonthDisplayName,
    isCurrentMonth,
    navigateMonth
} from '../utils/dateUtils';
import { monthAPI, onBudgetContextChange } from '../services/api';

/**
 * Hook for managing month selection and navigation.
 *
 * @param {string|null} budgetStartMonth - Budget start month in YYYY-MM-DD format
 * @param {string|null} budgetCeaseDate - Budget cease date in YYYY-MM-DD format
 * @returns {Object} Month navigation state and operations
 * @returns {Date} return.selectedMonth - Currently selected month
 * @returns {Function} return.setSelectedMonth - Manually set selected month
 * @returns {Function} return.handleNavigateMonth - Navigate prev/next
 * @returns {Function} return.goToCurrentMonth - Jump to current month
 * @returns {string} return.displayName - Formatted month name (e.g., "October 2025")
 * @returns {boolean} return.isPrevDisabled - Whether prev button is disabled
 * @returns {boolean} return.isNextDisabled - Whether next button is disabled
 * @returns {boolean} return.isClosed - Whether month is closed
 * @returns {boolean} return.isCeased - Whether budget is ceased
 * @returns {Function} return.handleCloseMonth - Close the month (owner only)
 * @returns {Function} return.handleOpenMonth - Open the month (owner only)
 *
 * @example
 * const monthNav = useMonthNavigation(budgetInfo?.startMonth, budgetInfo?.ceaseDate);
 * console.log(monthNav.displayName); // "October 2025"
 * monthNav.handleNavigateMonth('prev'); // Go to previous month
 * if (!monthNav.isPrevDisabled) monthNav.handleNavigateMonth('prev');
 */
export const useMonthNavigation = (budgetStartMonth = null, budgetCeaseDate = null) => {
    // Month navigation state (using JavaScript Date object)
    const [selectedMonth, setSelectedMonth] = useState(() => {
        return getCurrentMonth();
    });

    // Track if current month is closed
    const [isClosed, setIsClosed] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [closeError, setCloseError] = useState(null);

    // Convert budgetStartMonth string to Date for comparison
    const startMonthDate = useMemo(() => {
        if (!budgetStartMonth) return null;
        // Parse the date string and create a local date at midnight
        const [year, month] = budgetStartMonth.split('-');
        return new Date(parseInt(year), parseInt(month) - 1, 1);
    }, [budgetStartMonth]);

    // Convert budgetCeaseDate string to Date for comparison
    const ceaseDateObject = useMemo(() => {
        if (!budgetCeaseDate) return null;
        // Parse the date string and create a local date at midnight
        const [year, month] = budgetCeaseDate.split('-');
        return new Date(parseInt(year), parseInt(month) - 1, 1);
    }, [budgetCeaseDate]);

    // Check if budget is ceased
    const isCeased = useMemo(() => {
        return budgetCeaseDate !== null;
    }, [budgetCeaseDate]);

    // Check if previous button should be disabled
    const isPrevDisabled = useMemo(() => {
        if (!startMonthDate) return false;

        // Compare year and month
        return selectedMonth.getFullYear() === startMonthDate.getFullYear() &&
               selectedMonth.getMonth() === startMonthDate.getMonth();
    }, [selectedMonth, startMonthDate]);

    // Check if next button should be disabled
    const isNextDisabled = useMemo(() => {
        // If budget is ceased, cannot navigate past the cease date month
        if (ceaseDateObject) {
            return selectedMonth.getFullYear() === ceaseDateObject.getFullYear() &&
                   selectedMonth.getMonth() === ceaseDateObject.getMonth();
        }
        // Otherwise, cannot navigate past current month
        return isCurrentMonth(selectedMonth);
    }, [selectedMonth, ceaseDateObject]);

    // Navigate to previous/next month
    const handleNavigateMonth = (direction) => {
        // Prevent navigation if disabled
        if (direction === 'prev' && isPrevDisabled) return;
        if (direction === 'next' && isNextDisabled) return;

        setSelectedMonth(prevMonth => navigateMonth(prevMonth, direction));
    };

    // Go to current month
    const goToCurrentMonth = () => {
        setSelectedMonth(getCurrentMonth());
    };

    // Check if we're viewing current month
    const isViewingCurrentMonth = () => {
        return isCurrentMonth(selectedMonth);
    };

    // Get display name for current month
    const getSelectedMonthDisplayName = () => {
        return getMonthDisplayName(selectedMonth);
    };

    // Convert Date to month key (YYYY-MM format)
    const getMonthKey = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    };

    // Fetch month status when selectedMonth changes
    useEffect(() => {
        const fetchMonthStatus = async () => {
            // Wait for budget context to be set before loading
            const { transactionAPI } = require('../services/api');
            if (!transactionAPI.getCurrentBudgetId()) {
                return;
            }

            try {
                const monthKey = getMonthKey(selectedMonth);
                const balance = await monthAPI.getBalance(monthKey);
                setIsClosed(balance.isClosed);
                setCloseError(null);
            } catch (error) {
                // Error handling
                setIsClosed(false);
            }
        };

        fetchMonthStatus();

        // Subscribe to budget context changes and reload when it's set
        const unsubscribe = onBudgetContextChange((budgetId) => {
            if (budgetId) {
                fetchMonthStatus();
            }
        });

        return unsubscribe;
    }, [selectedMonth]);

    // Close current month
    const handleCloseMonth = async () => {
        setIsClosing(true);
        setCloseError(null);
        try {
            const monthKey = getMonthKey(selectedMonth);
            await monthAPI.close(monthKey);
            setIsClosed(true);
        } catch (error) {
            // Error handling
            setCloseError('Closing Failed');
        } finally {
            setIsClosing(false);
        }
    };

    // Open current month
    const handleOpenMonth = async () => {
        setIsClosing(true);
        setCloseError(null);
        try {
            const monthKey = getMonthKey(selectedMonth);
            await monthAPI.open(monthKey);
            setIsClosed(false);
        } catch (error) {
            // Error handling
            setCloseError('Opening Failed');
        } finally {
            setIsClosing(false);
        }
    };

    return {
        selectedMonth,
        setSelectedMonth,
        handleNavigateMonth,
        goToCurrentMonth,
        isViewingCurrentMonth,
        getSelectedMonthDisplayName,
        isPrevDisabled,
        isNextDisabled,
        isClosed,
        isCeased,
        isClosing,
        closeError,
        handleCloseMonth,
        handleOpenMonth
    };
};